import { Bell, User, Settings, LogOut, Languages, Clock, Banknote } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useBrandingTheme } from '@/hooks/useBrandingTheme';
import { useState, useEffect } from 'react';
import { TenantSwitcher } from './TenantSwitcher';

const PHONE_PREFIX_TO_COUNTRY: Record<string, { flag: string; name: string; nameAr: string }> = {
  '+249': { flag: '🇸🇩', name: 'Sudan', nameAr: 'السودان' },
  '+966': { flag: '🇸🇦', name: 'Saudi Arabia', nameAr: 'السعودية' },
  '+971': { flag: '🇦🇪', name: 'UAE', nameAr: 'الإمارات' },
  '+20': { flag: '🇪🇬', name: 'Egypt', nameAr: 'مصر' },
  '+962': { flag: '🇯🇴', name: 'Jordan', nameAr: 'الأردن' },
  '+968': { flag: '🇴🇲', name: 'Oman', nameAr: 'عُمان' },
  '+974': { flag: '🇶🇦', name: 'Qatar', nameAr: 'قطر' },
  '+965': { flag: '🇰🇼', name: 'Kuwait', nameAr: 'الكويت' },
  '+973': { flag: '🇧🇭', name: 'Bahrain', nameAr: 'البحرين' },
  '+218': { flag: '🇱🇾', name: 'Libya', nameAr: 'ليبيا' },
  '+212': { flag: '🇲🇦', name: 'Morocco', nameAr: 'المغرب' },
  '+216': { flag: '🇹🇳', name: 'Tunisia', nameAr: 'تونس' },
  '+213': { flag: '🇩🇿', name: 'Algeria', nameAr: 'الجزائر' },
  '+964': { flag: '🇮🇶', name: 'Iraq', nameAr: 'العراق' },
};

function useTenantClock(timezone: string | null) {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tz = timezone || 'Africa/Khartoum';
    const update = () => {
      try {
        setTime(new Date().toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' }));
      } catch {
        setTime(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      }
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [timezone]);
  return time;
}

export function Header() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, tenantCurrency, tenantTimezone, tenantPhonePrefix } = useAuthStore();
  const { language, setLanguage } = useUIStore();
  const { isSuperAdmin } = useTenantContext();
  const { appName, appNameArabic } = useBrandingTheme();
  const currentTime = useTenantClock(tenantTimezone);

  const country = PHONE_PREFIX_TO_COUNTRY[tenantPhonePrefix || '+249'];
  const isArabic = language === 'ar';

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'م';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2);
  };

  return (
    <header className="h-[60px] bg-background border-b px-6 flex items-center justify-between sticky top-0 z-30">
      {/* Tenant Context Info */}
      {!isSuperAdmin ? (
        <div className="hidden md:flex items-center gap-4">
          {/* Tenant Name */}
          <span className="text-sm font-semibold text-foreground">
            {(isArabic ? appNameArabic : appName) || appName || ''}
          </span>

          <div className="h-5 w-px bg-border" />

          {/* Country */}
          {country && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="text-base">{country.flag}</span>
              <span>{isArabic ? country.nameAr : country.name}</span>
            </div>
          )}

          {/* Currency */}
          {tenantCurrency && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Banknote className="h-3.5 w-3.5" />
              <span className="font-medium">{tenantCurrency}</span>
            </div>
          )}

          {/* Timezone & Clock */}
          {currentTime && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-mono tabular-nums">{currentTime}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="hidden md:flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">
            {t('layout.header.platformAdmin')}
          </span>
          {/* Chunk 2d — SuperAdmin tenant picker. Sets impersonatedTenantId on
              the auth store; apiClient appends X-Impersonate-Tenant on every
              request after that. Clicking "All tenants" clears the override. */}
          <TenantSwitcher />
        </div>
      )}

      {/* Right Actions */}
      <div className="flex items-center gap-4">
        {/* Notifications - Only for TenantAdmin (tenant-specific notifications) */}
        {!isSuperAdmin && (
          <Button
            variant="ghost"
            size="icon"
            className="relative text-muted-foreground hover:text-primary"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-2 end-2 h-2 w-2 bg-danger rounded-full border-2 border-background"></span>
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-muted-foreground hover:text-primary border-muted-foreground/30"
          onClick={toggleLanguage}
        >
          <Languages className="h-4 w-4" />
          <span className="font-medium">{language === 'ar' ? 'EN' : 'ع'}</span>
        </Button>

        <div className="flex items-center gap-3 border-e pe-4 me-2">
          <div className="text-start hidden sm:block">
            <p className="text-sm font-semibold leading-none">
              {user?.fullName || t('layout.header.user')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('layout.header.admin')}</p>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10 border border-border">
                  <AvatarImage src={user?.profileImage} alt={user?.fullName} />
                  <AvatarFallback>{getInitials(user?.fullName)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user?.fullName || t('layout.header.user')}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.mobileNo || 'admin@maksab.com'}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={isSuperAdmin ? '/platform-settings' : '/settings'} className="flex items-center cursor-pointer">
                  <User className="ms-2 h-4 w-4" />
                  {t('layout.header.profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={isSuperAdmin ? '/platform-settings' : '/settings'} className="flex items-center cursor-pointer">
                  <Settings className="ms-2 h-4 w-4" />
                  {t('layout.header.settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger focus:text-danger cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="ms-2 h-4 w-4" />
                {t('layout.header.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
